import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal, Form, Input, Select, InputNumber, Checkbox } from 'antd';
import { AddressAutocomplete } from '../../components/AddressAutocomplete/AddressAutocomplete';
import { isMetropolitanZipcode, isValidZipcode } from '../../utils/zipcode';
import useErrorMessage from '../../utils/ErrorMessage';
import useAuthContext from '../../contexts/AuthContext';

const PatchMRIWKAddressModal = ({
  visible = false,
  onClose = () => {},
  onPatch = () => {},
  address = {}
}) => {
  const { t } = useTranslation();
  const { dispatchAPI } = useAuthContext();
  const [form] = Form.useForm();

  const [cities, setCities] = useState([]);
  const [citiesLoading, setCitiesLoading] = useState(false);

  const postalCodeWatcher = Form.useWatch('postal_code', form);

  const updateCity = (city) => {
    form.setFieldsValue({ city });
  };

  const getWakamCities = async (postalCode) => {
    setCitiesLoading(true);

    try {
      const { data: cities } = await dispatchAPI('GET', {
        url: `/wakam/cities?postal_code=${postalCode}`
      });

      const mappedCities = cities.map((city) => ({
        value: city,
        label: city
      }));

      setCities(mappedCities || []);

      if (mappedCities.length > 0) {
        updateCity(mappedCities[0].value);
        form.validateFields(['postal_code']);
      }
    } catch (error) {
      setCities([]);
      updateCity(null);
    } finally {
      setCitiesLoading(false);
    }
  };

  const handleSubmit = async (formValues) => {
    if (typeof onPatch === 'function') {
      onPatch({ address: formValues });
    }
  };

  useEffect(() => {
    if (
      isValidZipcode(postalCodeWatcher) &&
      isMetropolitanZipcode(postalCodeWatcher)
    ) {
      getWakamCities(postalCodeWatcher);
    } else {
      setCities([]);
      updateCity(null);
    }
  }, [postalCodeWatcher]);

  return (
    <Modal
      title={t('settings.tickets.modal_exemption.mriwk_address')}
      open={visible}
      onCancel={onClose}
      centered
      onOk={form.submit}
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          country: 'France',
          ...address
        }}
        onFinish={handleSubmit}
      >
        <Form.Item
          name={['number']}
          label={t('contracts.form.address.number')}
          rules={[{ required: true }]}
        >
          <InputNumber stringMode />
        </Form.Item>

        <Form.Item
          name={['street']}
          label={t('contracts.form.address.street')}
          rules={[{ required: true }]}
        >
          <Input />
        </Form.Item>

        <Form.Item
          name={['additional']}
          label={t('contracts.form.address.additional')}
        >
          <Input />
        </Form.Item>

        <Form.Item
          name={['postal_code']}
          label={t('contracts.form.address.postal_code')}
          status="validating"
          rules={[
            { required: true },
            {
              validator: async (_, value) => {
                if (!isValidZipcode(value)) {
                  return Promise.reject("Ce code postal n'est pas valide");
                }

                return Promise.resolve();
              }
            },
            {
              validator: async (_, value) => {
                if (isValidZipcode(value) && !isMetropolitanZipcode(value)) {
                  return Promise.reject("Ce code postal n'est pas éligible");
                }

                return Promise.resolve();
              }
            },
            {
              validator: async (_, value) => {
                if (isValidZipcode(value) && cities.length === 0) {
                  return Promise.reject("Ce code postal n'est pas valide.");
                }

                return Promise.resolve();
              }
            }
          ]}
        >
          <Input />
        </Form.Item>

        <Form.Item
          name={['city']}
          label={t('contracts.form.address.city')}
          rules={[{ required: true }]}
        >
          <Select options={cities} defaultActiveFirstOption />
        </Form.Item>

        <Form.Item
          name={['country']}
          label={t('contracts.form.address.country')}
          rules={[{ required: true }]}
          initialValue="France"
          hidden
        >
          <Input />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default PatchMRIWKAddressModal;
